import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Logo, Button, Field } from '../components/sv';

export function AuthShell({ title, text, children, image = '/img/bridge-officer.jpg', testId }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid={testId}>
      <div className="flex flex-col">
        <div className="h-[72px] flex items-center px-6 md:px-10 border-b border-gray-200"><Link to="/"><Logo /></Link></div>
        <div className="flex-1 flex items-center px-6 md:px-10 py-12">
          <div className="w-full max-w-[440px]">
            <h1 className="text-[36px] md:text-[40px] sv-display">{title}</h1>
            {text && <p className="text-gray-700 mt-3">{text}</p>}
            <div className="mt-8">{children}</div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-gray-200">
        <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="text-[28px] sv-display text-white max-w-md">Every port, on time.</p>
          <p className="text-white/75 mt-2 text-[15px]">343 vessels. 121 countries. One network.</p>
        </div>
      </div>
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const from = location.state?.from?.pathname || '/dashboard';
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  function handleChange(e) { setFormData(function (p) { return { ...p, [e.target.name]: e.target.value }; }); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.email || !formData.password) { toast.error('Enter your email and password'); return; }
    setLoading(true);
    try {
      const userData = await login(formData.email, formData.password);
      toast.success('Signed in');
      let redirectTo = from;
      if (from === '/dashboard') {
        if (userData.role === 'admin') redirectTo = '/admin';
        else if (userData.role === 'agent') redirectTo = '/agent';
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign in failed');
    } finally { setLoading(false); }
  }

  return (
    <AuthShell title="Log in" text="Access Compass, the customer portal, or the Bridge operations console." testId="login-page">
      <form onSubmit={handleSubmit} className="grid gap-5">
        <Field label="Email">
          <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="name@company.com" className="sv-input" data-testid="login-email" autoComplete="email" />
        </Field>
        <Field label="Password">
          <span className="relative block">
            <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} placeholder="Your password" className="sv-input pr-11" data-testid="login-password" autoComplete="current-password" />
            <button type="button" onClick={function () { setShowPassword(!showPassword); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-ink" aria-label="Toggle password visibility">{showPassword ? <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} /> : <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />}</button>
          </span>
        </Field>
        <Button type="submit" size="lg" disabled={loading} className="w-full mt-1" data-testid="login-submit">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Log in'}</Button>
        <p className="text-[15px] text-gray-700">No account yet? <Link to="/register" className="sv-link" data-testid="register-link">Open an account</Link></p>
      </form>
    </AuthShell>
  );
}

export default LoginPage;
